import { SiteHeroProps } from "@/components/SiteHero";
import heroHome from "@/assets/hero-Angela-ComplyHub.webp.asset.json";
import heroPricing from "@/assets/hero-complyhub-pricing.webp";
import heroDemo from "@/assets/hero-complyhub-demo.webp";
import heroBlog from "@/assets/hero-blog.webp";
import heroFaqs from "@/assets/hero-faqs.webp";
import heroResources from "@/assets/hero-resources.webp";
import heroFeatures2 from "@/assets/hero-complyhub-features-2.webp";
import heroStandardsScorecard from "@/assets/hero-standards-scorecard-poster.png";
import heroAngela2 from "@/assets/hero-Angela-ComplyHub-2.webp.asset.json";
import complybotDemo from "@/assets/ComplyHub-Complybot-Demo.png.asset.json";

/**
 * Centralized hero configuration for all marketing pages.
 * Edit copy here for site-wide consistency.
 */
export const HERO: Record<string, SiteHeroProps> = {
  home: {
    eyebrow: "Compliance Made Simple",
    title: "ComplyHub — RTO Compliance Software\nbuilt by someone who's actually\npassed the audit.",
    subtitle:
      "ComplyHub was built by a founder who has owned and operated three RTOs — not adapted from generic compliance software. Every register maps to the Standards for RTOs 2025.",
    primaryCta: { 
      label: "Start Free Trial", 
      href: "/trial-signup",
      trackingId: "hero-primary"
    },
    secondaryCta: { 
      label: "Book a demo", 
      href: "/demo#book",
      trackingId: "hero-secondary"
    },
    imageSrc: heroHome.url,
    mediaAlt: "Angela Connell-Richards — founder of ComplyHub",
    videoAspect: "9:16",
    mediaLabel: "Standards mapping with real-time audit visibility",
    mediaCaption: "Auto-maps your scope to the Outcome Standards — updated for 2025.",
    showWave: true,
    showSocialAvatars: false,
    socialProofText:
      "Join Australian RTOs already building audit-ready compliance with ComplyHub",
  },

  product: {
    eyebrow: "Product Overview",
    title: "ComplyHub RTO Compliance Software: Your RTO, Audit-Ready, Always.",
    subtitle:
      "ComplyHub.ai is Australia's only AI-native compliance platform purpose-built for the Standards for RTOs 2025 — replacing spreadsheets, shared drives, and compliance chaos with one structured system your whole team can use.",
    riskLine: "Designed to protect your RTO before the next audit.",
    standardsLine: "Aligned to 2025 Standards and audit-ready workflows.",
    primaryCta: { 
      label: "Start Free Trial", 
      href: "/trial-signup",
      trackingId: "product_hero"
    },
    secondaryCta: { 
      label: "Book a demo", 
      href: "/demo#book",
      trackingId: "product_hero"
    },
    imageSrc: heroAngela2.url,
    mediaAlt: "ComplyHub Trainers Matrix — credentials and currency tracked in real time",
    mediaLabel: "Trainers Matrix",
    mediaCaption: "39 trainers. 5 risk flags. Every credential tracked in real time.",
    showWave: true,
  },

  features: {
    eyebrow: "Features by Role",
    title: "ComplyHub RTO Compliance Software Features for Every Role.",
    subtitle:
      "Registers, dashboards, and workflows built around the Standards for RTOs 2025 — by compliance experts who know exactly what ASQA is looking for.",
    primaryCta: { 
      label: "Book a demo", 
      href: "/demo#book",
      trackingId: "features_hero"
    },
    secondaryCta: { 
      label: "Start free trial", 
      href: "/trial-signup",
      trackingId: "features_hero"
    },
    imageSrc: heroFeatures2,
    mediaAlt: "ComplyHub platform features",
    videoAspect: "9:16",
    mediaLabel: "Features preview",
    mediaCaption: "Registers, dashboards, and workflows for the 2025 Standards.",
    showWave: true,
    showSocialAvatars: false,
    socialProofText: "",
  },

  pricing: {
    eyebrow: "Transparent Pricing",
    title: "Audit-Ready Compliance at Every Scale.",
    subtitle:
      "Choose the plan that fits your RTO. Every plan includes the full compliance system — no locked features, no surprises.",
    riskLine: "Choose the level of control your RTO needs to stay audit-ready.",
    standardsLine: "Aligned to 2025 Standards and audit-ready workflows.",
    primaryCta: {
      label: "Start free trial",
      href: "/trial-signup",
      trackingId: "pricing_hero"
    },
    secondaryCta: {
      label: "Book a demo",
      href: "/demo#book",
      trackingId: "pricing_hero"
    },
    imageSrc: heroPricing,
    mediaAlt: "ComplyHub pricing",
    videoAspect: "9:16",
    mediaLabel: "Platform preview",
    showWave: true,
  },

  compliance: {
    eyebrow: "Standards Alignment",
    title: "Compliance, Built Into How You Work.",
    subtitle:
      "ComplyHub.ai is purpose-built for the Standards for RTOs 2025 — so every register, document, and workflow your team uses is structured around the Outcome Standards and Compliance Requirements from day one. Standards alignment is not a feature. It is the architecture.",
    primaryCta: {
      label: "Start Free Trial",
      href: "/trial-signup",
      trackingId: "compliance_hero"
    },
    secondaryCta: {
      label: "Book a Demo",
      href: "/demo#book",
      trackingId: "compliance_hero"
    },
    imageSrc: heroStandardsScorecard,
    mediaAlt: "Angela Connell-Richards — compliance expert",
    mediaLabel: "Compliance preview",
    mediaCaption: "Compliance problems rarely appear without warning — ComplyHub makes sure you see them first.",
    showWave: true,
  },

  blog: {
    eyebrow: "Compliance Insights",
    title: "Compliance Insights for Australian RTOs.",
    subtitle:
      "Practical guidance on the Standards for RTOs 2025, ASQA audits, and governance — written by compliance experts who have been in your shoes.",
    primaryCta: {
      label: "Start free trial",
      href: "/trial-signup",
      trackingId: "blog_hero"
    },
    secondaryCta: {
      label: "Book a demo",
      href: "/demo#book",
      trackingId: "blog_hero"
    },
    imageSrc: heroBlog,
    mediaAlt: "RTO compliance articles and resources",
    videoAspect: "9:16",
    mediaLabel: "Latest insights",
    showWave: true,
  },

  faq: {
    eyebrow: "Frequently Asked Questions",
    title: "Your Compliance Questions, Answered.",
    subtitle:
      "Clear answers on the Standards for RTOs 2025, ASQA audits, Training and Assessment Strategies, and ComplyHub — no jargon, no runaround.",
    primaryCta: {
      label: "Book a Demo",
      href: "/demo#book",
      trackingId: "faq_hero"
    },
    secondaryCta: {
      label: "Start free trial",
      href: "/trial-signup",
      trackingId: "faq_hero"
    },
    imageSrc: heroFaqs,
    mediaAlt: "RTO compliance frequently asked questions",
    videoAspect: "9:16",
    mediaLabel: "Common questions",
    showWave: true,
  },

  resources: {
    eyebrow: "Free Resources",
    title: "Free Tools to Get Your RTO Audit-Ready.",
    subtitle:
      "Guides, checklists, and tools to help your RTO stay audit-ready under the Standards for RTOs 2025 — all in one place.",
    primaryCta: {
      label: "Start free trial",
      href: "/trial-signup",
      trackingId: "resources_hero"
    },
    secondaryCta: {
      label: "Book a demo",
      href: "/demo#book",
      trackingId: "resources_hero"
    },
    imageSrc: heroResources,
    mediaAlt: "RTO compliance resources",
    videoAspect: "9:16",
    mediaLabel: "Compliance library",
    showWave: true,
  },

  maturityCheckComplete: {
    eyebrow: "Scorecard Complete",
    title: "Your Compliance Scorecard Results Are Ready",
    subtitle: "You now have a clear view of where your system holds and where it breaks.",
    primaryCta: {
      label: "Book a Demo",
      href: "/demo#book",
      trackingId: "maturity_complete_hero"
    },
    secondaryCta: {
      label: "Start Free Trial",
      href: "/trial-signup",
      trackingId: "maturity_complete_hero"
    },
    showWave: true,
  },

  demo: {
    eyebrow: "Book a Demo",
    title: "See ComplyHub in Action.",
    subtitle:
      "Book a personalised demo and see exactly how ComplyHub handles your RTO's compliance obligations — from TAS generation to audit preparation.",
    riskLine: "See how to reduce audit risk before your next review.",
    standardsLine: "Aligned to 2025 Standards and audit-ready workflows.",
    bullets: [
      "Standards mapping and self-assurance view",
      "Evidence and document management",
      "Audit-ready outputs and reporting",
    ],
    primaryCta: {
      label: "Start Free Trial",
      href: "/trial-signup",
      trackingId: "demo_hero"
    },
    imageSrc: heroDemo,
    mediaAlt: "Book a ComplyHub demo",
    videoAspect: "9:16",
    mediaLabel: "Platform preview",
    showWave: true,
  },

  complybot: {
    eyebrow: "Platform Feature",
    title: "The Compliance Assistant Built for Australian RTOs.",
    subtitle:
      "ComplyBot answers Standards questions, drafts documents, and surfaces regulatory obligations — without you leaving the platform. Powered by Claude (Anthropic). Purpose-built for the VET sector.",
    primaryCta: {
      label: "Start Free Trial →",
      href: "/pricing",
      trackingId: "complybot-hero",
    },
    secondaryCta: {
      label: "Book a Demo",
      href: "/demo#book",
      trackingId: "complybot-hero",
    },
    imageSrc: complybotDemo.url,
    mediaAlt:
      "Angela Connell-Richards presenting ComplyBot — ComplyHub's AI compliance assistant for Australian RTOs",
    showSocialAvatars: false,
    socialProofText: "",
    showWave: true,
  },
} as const;
